// Preserve the localized SDS app proxy without browser-side JSX compilation.
if (!customElements.get('safety-data-sheet-search')) {
  customElements.define('safety-data-sheet-search', class extends HTMLElement {
    connectedCallback() { queueMicrotask(() => this.initialize()); }

    initialize() {
      if (!this.isConnected || this.controller) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.input = this.form.querySelector('input');
      this.results = this.querySelector('[data-sds-results]');
      this.detail = this.querySelector('[data-sds-detail]');
      this.search = this.querySelector('[data-sds-search]');
      this.downloads = this.querySelector('[data-sds-downloads]');
      this.error = this.querySelector('[data-sds-error]');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (!this.busy && this.form.reportValidity()) this.searchSheets();
      }, options);
      this.addEventListener('click', event => {
        const action = event.target.closest('[data-sds-action]');
        if (!action || !this.contains(action)) return;
        if (action.dataset.sdsAction === 'select') this.selectItem(this.items[Number(action.dataset.index)]);
        else if (action.dataset.sdsAction === 'retry') this.selectItem(this.selectedItem);
        else this.returnToResults(action.dataset.sdsAction === 'new');
      }, options);
      if (!this.initialized) {
        this.initialized = true;
        const params = new URLSearchParams(window.location.search);
        if (params.has('code') && params.has('prodname')) {
          // URLSearchParams decodes once. Preserve the legacy SKU-prefix rule.
          const code = params.get('code').replace(/[A-Za-z]/g, '').trim();
          if (code) this.selectedItem = { code, name: params.get('prodname') };
        }
      }
      if (this.selectedItem) this.selectItem(this.selectedItem);
      else this.setBusy(false);
    }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      this.form.querySelector('[type="submit"]').disabled = busy;
      this.querySelector('[data-sds-loading]').hidden = !busy;
    }

    cancelRequest() {
      this.request?.abort();
      this.request = null;
      this.sequence = (this.sequence || 0) + 1;
      if (this.form) this.setBusy(false);
    }

    async run(operation, onSuccess) {
      this.cancelRequest();
      const sequence = this.sequence;
      this.request = new AbortController();
      const signal = this.request.signal;
      this.error.hidden = true;
      this.querySelector('[data-sds-action="retry"]').hidden = true;
      this.setBusy(true);
      try {
        const result = await operation(signal);
        if (!this.isConnected || sequence !== this.sequence) return;
        if (!Array.isArray(result)) throw new Error('Invalid SDS response');
        onSuccess(result);
      } catch (error) {
        if (sequence !== this.sequence || !this.isConnected || error.name === 'AbortError') return;
        this.error.textContent = 'Unable to load safety data sheets. Please try again.';
        this.error.hidden = false;
        this.querySelector('[data-sds-action="retry"]').hidden = !this.selectedItem;
      } finally {
        if (sequence === this.sequence && this.isConnected) this.setBusy(false);
      }
    }

    searchSheets() {
      const term = this.input.value.trim();
      if (!term) return;
      this.selectedItem = null;
      this.results.replaceChildren();
      return this.run(signal => window.api.searchSafetyDataSheets(term, { signal }), items => {
        this.items = items;
        const summary = document.createElement('p');
        summary.className = 'pe-text-base';
        summary.textContent = 'Your search for "' + term + '" yielded ' + items.length + ' results:';
        this.results.appendChild(summary);
        if (!items.length) return;
        const table = document.createElement('table');
        table.id = 'sds-search-results';
        table.className = 'pe-table pe-table-auto pe-border-collapse pe-w-full pe-border pe-border-solid pe-border-[#f1f1f1]';
        table.innerHTML = '<thead><tr><th scope="col" class="pe-text-start pe-px-2 pe-text-sm pe-font-bold pe-text-[#414653] pe-py-2">Item Number</th><th scope="col" class="pe-text-start pe-px-2 pe-text-sm pe-font-bold pe-text-[#414653] pe-py-2">Product Name</th></tr></thead><tbody></tbody>';
        const body = table.querySelector('tbody');
        items.forEach((item, index) => {
          const row = document.createElement('tr');
          row.className = 'even:pe-bg-[#f1f1f1]';
          for (const value of [item.code, item.name]) {
            const cell = document.createElement('td');
            cell.className = 'pe-leading-tight';
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'pe-px-2 pe-text-sm pe-text-black pe-underline pe-cursor-pointer pe-bg-transparent pe-border-0 pe-text-start';
            button.dataset.sdsAction = 'select';
            button.dataset.index = index;
            button.textContent = value;
            cell.appendChild(button);
            row.appendChild(cell);
          }
          body.appendChild(row);
        });
        this.results.appendChild(table);
      });
    }

    selectItem(item) {
      if (!item) return;
      this.selectedItem = item;
      this.search.hidden = true;
      this.detail.hidden = false;
      const title = this.querySelector('[data-sds-title]');
      title.textContent = 'Available SDS sheets for ' + item.name;
      title.focus({ preventScroll: true });
      this.downloads.replaceChildren();
      return this.run(signal => window.api.getSafetyDataSheets(item.code, { signal }), downloads => {
        if (!downloads.length) {
          this.downloads.textContent = 'No SDS sheets available for this product.';
          return;
        }
        const list = document.createElement('ul');
        list.className = 'pe-m-0 pe-p-0 pe-list-none pe-py-4';
        for (const download of downloads) {
          const url = new URL(download.url, window.location.href);
          if (!['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid SDS download URL');
          const item = document.createElement('li');
          item.className = 'pe-leading-5';
          const link = document.createElement('a');
          link.className = 'pe-text-black pe-text-sm';
          link.href = url.href;
          link.textContent = download.name;
          item.appendChild(link);
          list.appendChild(item);
        }
        this.downloads.appendChild(list);
      });
    }

    returnToResults(reset) {
      this.cancelRequest();
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('prodname');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      this.selectedItem = null;
      this.search.hidden = false;
      this.detail.hidden = true;
      this.error.hidden = true;
      if (reset) { this.input.value = ''; this.results.replaceChildren(); this.items = []; }
      this.input.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.cancelRequest();
      this.controller = null;
    }
  });
}
